import { Button, Icon, Spinner, StatusIndicator } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { logUserClick } from 'src/analytics/KatalLogger';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';
import { usePnlReportList } from '../pnl-reports/hooks/usePnlReportList';

interface PnlReportsNavDropdownProps {
  readonly isActive: boolean;
}

interface PanelPosition {
  readonly top: number;
  readonly right: number;
}

const PANEL_WIDTH = 380;
const PANEL_GAP = 6;

export const PnlReportsNavDropdown: React.FC<PnlReportsNavDropdownProps> = ({ isActive }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { reports, isLoading, error } = usePnlReportList();

  const sortedReports = useMemo(
    () => [...reports].sort((a, b) => a.report_name.localeCompare(b.report_name, undefined, { sensitivity: 'base' })),
    [reports]
  );

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Position the portal-rendered panel relative to the trigger. Recompute on open,
  // resize, and scroll so it stays anchored while the layout shifts.
  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + PANEL_GAP,
      right: Math.max(8, window.innerWidth - rect.right)
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, close]);

  const goTo = useCallback(
    (path: string, actionLabel: string) => {
      logUserClick(actionLabel, 'Top Navigation', 'Landing Page');
      close();
      navigateWithEmbedParams(navigate, path);
    },
    [navigate, close]
  );

  const handleReportClick = (reportName: string) =>
    goTo(`/pnl-reports/${encodeURIComponent(reportName)}`, `Opened report "${reportName}" from P&L Reports Navigation`);

  const handleCreate = () => goTo('/pnl-reports/create', 'Selected Create report in P&L Reports Navigation');

  const handleBrowseAll = () => goTo('/pnl-reports', 'Selected Browse all reports in P&L Reports Navigation');

  const panel = isOpen && position && (
    <div
      ref={panelRef}
      className="pnl-reports-nav-panel"
      role="menu"
      aria-label="P&L Reports"
      style={{ top: position.top, right: position.right, width: PANEL_WIDTH }}
    >
      <div className="pnl-reports-nav-list">
        {isLoading && (
          <div className="pnl-reports-nav-state">
            <Spinner /> <span>Loading reports…</span>
          </div>
        )}
        {!isLoading && error && (
          <div className="pnl-reports-nav-state">
            <StatusIndicator type="error">Failed to load reports</StatusIndicator>
          </div>
        )}
        {!isLoading && !error && sortedReports.length === 0 && (
          <div className="pnl-reports-nav-state pnl-reports-nav-empty">No reports yet.</div>
        )}
        {!isLoading &&
          !error &&
          sortedReports.map((r) => (
            <button
              type="button"
              key={r.pnl_report_id}
              className="pnl-reports-nav-item"
              onClick={() => handleReportClick(r.report_name)}
              role="menuitem"
              title={r.report_name}
            >
              <span className="pnl-reports-nav-item-name">{r.report_name}</span>
            </button>
          ))}
      </div>

      <div className="pnl-reports-nav-footer">
        <Button variant="primary" iconName="add-plus" onClick={handleCreate}>
          Create report
        </Button>
        <Button variant="normal" onClick={handleBrowseAll}>
          Browse all reports
        </Button>
      </div>
    </div>
  );

  return (
    <div className="pnl-reports-nav-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className={`pnl-reports-nav-trigger ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="pnl-reports-nav-trigger-label">P&amp;L Reports</span>
        <Icon name={isOpen ? 'caret-up-filled' : 'caret-down-filled'} />
      </button>
      {panel && createPortal(panel, document.body)}
    </div>
  );
};

export default PnlReportsNavDropdown;
